import { IconCloudUpload } from '@tabler/icons-react';
import Dropzone, { FileRejection } from 'react-dropzone';
import toast from 'react-hot-toast';
import { Button } from 'ui-kit';

export interface FileDropZoneProps {
  onFileDrop?: (file: File) => void;
  allowedFileTypes?: string[];
  width?: string;
  height?: string;
  disabled?: boolean;
  vertical?: boolean;
  allowedTypesMessage?: string;
  maxFiles?: number;
}

// Map MIME types to file extensions for fallback validation
const MIME_TO_EXTENSIONS: Record<string, string[]> = {
  'application/zip': ['.zip'],
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm'],
  'application/vnd.ms-excel.sheet.binary.macroEnabled.12': ['.xlsb'],
  'text/csv': ['.csv'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/tiff': ['.tif', '.tiff'],
};

// Validate file by extension when MIME type detection fails
const isValidFileByExtension = (fileName: string, allowedMimeTypes: string[]): boolean => {
  const fileExt = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
  return allowedMimeTypes.some((mimeType) =>
    MIME_TO_EXTENSIONS[mimeType]?.some((ext) => fileExt === ext),
  );
};

export const FileDropZone = ({
  onFileDrop,
  allowedFileTypes = [],
  width,
  height,
  disabled = false,
  vertical = false,
  allowedTypesMessage,
  maxFiles,
}: FileDropZoneProps) => {
  const handleDrop = (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
    // Handle files that were rejected by react-dropzone
    const stillRejected: FileRejection[] = [];
    const nowAccepted: File[] = [];

    rejectedFiles.forEach((fileRejection) => {
      const { file, errors } = fileRejection;

      // Only rescue files if the ONLY rejection reason is invalid MIME type
      // If there are other errors (size, count, etc.), respect those
      const onlyTypeError = errors.length === 1 && errors[0].code === 'file-invalid-type';

      if (
        onlyTypeError &&
        allowedFileTypes.length > 0 &&
        isValidFileByExtension(file.name, allowedFileTypes)
      ) {
        // File extension is valid, accept it despite MIME type mismatch
        nowAccepted.push(file);
      } else {
        // Still rejected for type or other reasons
        stillRejected.push(fileRejection);
      }
    });

    // Show errors for files that are still rejected
    stillRejected.forEach((fileRejection) => {
      const { file, errors } = fileRejection;
      errors.forEach((error) => {
        toast.error(`File ${file.name} rejected: ${error.message}`, {
          duration: 10000,
        });
      });
    });

    // Process all accepted files
    [...acceptedFiles, ...nowAccepted].forEach((file) => {
      onFileDrop?.(file);
    });
  };

  return (
    <Dropzone
      onDropAccepted={(acceptedFiles) => handleDrop(acceptedFiles, [])}
      onDropRejected={(rejectedFiles) => handleDrop([], rejectedFiles)}
      multiple={true}
      maxFiles={maxFiles || 20}
      accept={allowedFileTypes.join(',')}
      disabled={disabled}
    >
      {({ getRootProps, getInputProps, isDragActive }) => (
        <section>
          <div {...getRootProps()}>
            <input
              {...getInputProps({
                'aria-label': 'File upload',
                accept: allowedFileTypes.join(','),
              })}
              disabled={disabled}
              className="sr-only"
            />
            <div
              className={`flex flex-col items-center justify-center py-6 text-slate-100 transition-all duration-300 ${
                disabled
                  ? 'cursor-not-allowed'
                  : `cursor-pointer ${
                      isDragActive
                        ? 'bg-blue-900'
                        : 'bg-surface border-accent-emphasis hover:border-blue-400 hover:bg-slate-900'
                    }`
              }`}
              style={{
                width: width || '744px',
                height: height || '96px',
                backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='%23E028DD' stroke-width='2' stroke-dasharray='4%2c 14' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`,
                borderRadius: '8px',
              }}
            >
              <div className="flex flex-col gap-4 px-4">
                <div
                  className={`flex ${vertical ? 'flex-col' : ''} items-center justify-center ${
                    vertical ? 'gap-4' : 'gap-3'
                  }`}
                >
                  <IconCloudUpload stroke={2} className="text-blue-100" size={56} />
                  <span className="text-sm text-slate-200">Drag & Drop your file(s)</span>
                  <span className="text-sm text-slate-200">-or-</span>
                  <Button type="secondary" size="medium" label="browse" disabled={disabled}>
                    Browse to Upload
                  </Button>
                </div>
                {allowedTypesMessage && (
                  <div className="flex justify-center">
                    <span className="text-sm text-slate-400">{allowedTypesMessage}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}
    </Dropzone>
  );
};
